import { useEffect, useRef, useCallback, useState } from 'react';

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
const WARNING_DURATION = 60; // seconds before logout after warning shows
const STORAGE_KEY = 'session-timeout-minutes';

export type TimeoutMinutes = 5 | 15 | 30 | 60;

const getTimeoutMinutes = (): TimeoutMinutes => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    const val = parseInt(stored, 10);
    if (val === 5 || val === 15 || val === 30 || val === 60) return val as TimeoutMinutes;
  }
  return 30; // default
};

export const setTimeoutMinutes = (minutes: TimeoutMinutes) => {
  localStorage.setItem(STORAGE_KEY, String(minutes));
};

export const useInactivityTimeout = (
  isAuthenticated: boolean,
  onLogout: () => void
) => {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(WARNING_DURATION);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    timeoutRef.current = null;
    warningTimeoutRef.current = null;
    countdownRef.current = null;
  }, []);

  const resetTimer = useCallback(() => {
    if (!isAuthenticated) return;

    clearAllTimers();
    setShowWarning(false);
    setCountdown(WARNING_DURATION);

    const minutes = getTimeoutMinutes();
    const warningAt = (minutes * 60 - WARNING_DURATION) * 1000;

    // Show warning before timeout
    timeoutRef.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(WARNING_DURATION);

      // Start countdown
      let remaining = WARNING_DURATION;
      countdownRef.current = setInterval(() => {
        remaining -= 1;
        setCountdown(remaining);
        if (remaining <= 0) {
          clearAllTimers();
          setShowWarning(false);
          onLogout();
        }
      }, 1000);
    }, warningAt);
  }, [isAuthenticated, onLogout, clearAllTimers]);

  const stayLoggedIn = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearAllTimers();
      setShowWarning(false);
      return;
    }

    // Set initial timer
    resetTimer();

    // Track activity
    const handleActivity = () => {
      // Only reset if warning is not showing
      if (!warningTimeoutRef.current) {
        resetTimer();
      }
    };

    ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      clearAllTimers();
      ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [isAuthenticated, resetTimer, clearAllTimers]);

  return {
    showWarning,
    countdown,
    stayLoggedIn,
    currentTimeout: getTimeoutMinutes(),
  };
};
